import { BreadcrumbSwitcherMenu } from '@portal/core/components/breadcrumb-switcher-menu/BreadcrumbSwitcherMenu'
import graphql from 'babel-plugin-relay/macro'
import { Suspense, useEffect, useState } from 'react'
import { useLazyLoadQuery, useRefetchableFragment } from 'react-relay/hooks'
import { useNavigate } from 'react-router-dom'

import { DatabaseActionMenu_viewer$key } from './__generated__/DatabaseActionMenu_viewer.graphql'
import { DatabaseActionMenuQuery } from './__generated__/DatabaseActionMenuQuery.graphql'
import { DatabaseActionMenuRefetchQuery } from './__generated__/DatabaseActionMenuRefetchQuery.graphql'

const query = graphql`
  query DatabaseActionMenuQuery {
    viewer {
      ...DatabaseActionMenu_viewer
    }
  }
`

const refetchableApps = graphql`
  fragment DatabaseActionMenu_viewer on Viewer
  @refetchable(queryName: "DatabaseActionMenuRefetchQuery") {
    me {
      id
      apps {
        id
        name
        slug
        organizationSlug
      }
      organizations {
        id
        name
        slug
        apps {
          id
          name
          slug
        }
      }
    }
  }
`

type Props = {
  /** The current app slug */
  currentAppSlug: string
  /** The display text for the current app */
  currentAppName: string
  /** The organization slug, if in an org context */
  organizationSlug?: string
  /** Callback for building the URL to navigate to an app */
  getAppUrl: (appSlug: string, organizationSlug?: string) => string
  /** URL to the "all databases" list for the current tenant */
  allDatabasesUrl?: string
  /** URL to create a new database for the current tenant */
  newDatabaseUrl?: string
}

/**
 * An action menu for switching between databases in breadcrumbs.
 * Shows the current database with a dropdown to switch to others.
 */
export default function DatabaseActionMenu(props: Props) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Suspense
      fallback={
        <span className="text-foreground-subtle text-sm">
          {props.currentAppName}
        </span>
      }
    >
      <DatabaseActionMenuContent
        {...props}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </Suspense>
  )
}

function DatabaseActionMenuContent({
  currentAppSlug,
  currentAppName,
  organizationSlug,
  getAppUrl,
  allDatabasesUrl,
  newDatabaseUrl,
  isOpen,
  setIsOpen,
}: Props & {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const { viewer } = useLazyLoadQuery<DatabaseActionMenuQuery>(query, {})
  const [data, refetch] = useRefetchableFragment<
    DatabaseActionMenuRefetchQuery,
    DatabaseActionMenu_viewer$key
  >(refetchableApps, viewer)

  useEffect(() => {
    if (isOpen) {
      refetch({}, { fetchPolicy: 'store-and-network' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const me = data?.me
  const organizations = me?.organizations ?? []
  const personalApps = me?.apps ?? []

  // Show apps for the current organization, or personal apps when there is
  // no organization context.
  const currentOrg = organizationSlug
    ? organizations.find((org) => org?.slug === organizationSlug)
    : undefined
  const apps = organizationSlug ? (currentOrg?.apps ?? []) : personalApps

  const appItems = apps
    .filter((app) => !!app)
    .map((app) => ({
      id: app.id,
      label: app.name ?? '',
      onClick: () => {
        navigate(getAppUrl(app.slug ?? '', organizationSlug))
        setIsOpen(false)
      },
      active: currentAppSlug === app.slug,
    }))

  return (
    <BreadcrumbSwitcherMenu
      currentLabel={currentAppName}
      headerItem={
        allDatabasesUrl
          ? {
              label: 'All databases',
              onClick: () => {
                navigate(allDatabasesUrl)
                setIsOpen(false)
              },
            }
          : undefined
      }
      items={appItems}
      footerItem={
        newDatabaseUrl
          ? {
              label: 'New database',
              onClick: () => {
                navigate(newDatabaseUrl)
                setIsOpen(false)
              },
            }
          : undefined
      }
      onOpenChange={setIsOpen}
    />
  )
}
